import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Popover } from "antd";
import { RankingAPI } from "../../API/RankingAPI";
import {
  buildColumnLayout,
  extractPreferences,
  parseColumnWidth,
  toFrozenMap,
  toWidthMap,
} from "./gridPreferencesUtils";

const DEFAULT_MIN_WIDTH = 80;
const DEFAULT_SORT_PREFERENCES = {
  sort_by: "wpm_score",
  sort_dir: "desc",
};

// The saved layout arrives from an async call, so without a synchronous seed
// the grid paints once with the built-in default widths (and no frozen block)
// and then snaps to the persisted layout — a visible column shift on every
// load. Mirroring the last known layout locally lets the first paint already
// be correct; the API response still wins the moment it lands.
const GRID_LAYOUT_CACHE_KEY = "rankingGridColumnLayout";

const readCachedColumnLayout = () => {
  try {
    const raw = window.localStorage.getItem(GRID_LAYOUT_CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const writeCachedColumnLayout = (columnLayout) => {
  try {
    window.localStorage.setItem(
      GRID_LAYOUT_CACHE_KEY,
      JSON.stringify(columnLayout || {}),
    );
  } catch {
    // Best effort only — a full or disabled storage just means the next load
    // falls back to the pre-API defaults.
  }
};

const HEADER_HINT_CONTENT_STYLE = {
  fontWeight: 500,
  color: "#565656",
  fontSize: "15px",
  lineHeight: "1.5rem",
  margin: 0,
};

export const ColumnResizeHandle = ({ onMouseDown }) => (
  <Popover
    title=""
    content={<p style={HEADER_HINT_CONTENT_STYLE}>Drag to resize column</p>}
  >
    <span
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize column"
      className="column-resize-handle"
      onMouseDown={onMouseDown}
      onClick={(event) => event.stopPropagation()}
    />
  </Popover>
);

export const usePersistentColumnResize = ({
  columns = [],
  allColumnKeys = [],
  defaultWidthMap = {},
  minWidth = DEFAULT_MIN_WIDTH,
  messageToast,
  sortPreferences,
  gridPreferencesVersion = 0,
}) => {
  const [cachedColumnLayout] = useState(() => readCachedColumnLayout());
  const [widthMap, setWidthMap] = useState(() =>
    toWidthMap(cachedColumnLayout || {}),
  );
  const [frozenMap, setFrozenMap] = useState(() =>
    toFrozenMap(cachedColumnLayout || {}),
  );
  // Only the very first load can be gated: once true this never flips back, so
  // a later preference refresh (freeze apply) never remounts the table.
  const [preferencesReady, setPreferencesReady] = useState(
    () => cachedColumnLayout !== null,
  );
  const [sortPreferencesState, setSortPreferencesState] = useState(
    DEFAULT_SORT_PREFERENCES,
  );
  const widthMapRef = useRef(widthMap);
  const frozenMapRef = useRef(frozenMap);
  const sortPreferencesRef = useRef(sortPreferencesState);
  const hasLoadedPreferencesRef = useRef(false);
  const saveTimeoutRef = useRef(null);
  const userResizedRef = useRef(false);

  useEffect(() => {
    widthMapRef.current = widthMap;
  }, [widthMap]);

  useEffect(() => {
    frozenMapRef.current = frozenMap;
  }, [frozenMap]);

  useEffect(() => {
    sortPreferencesRef.current = sortPreferences ?? sortPreferencesState;
  }, [sortPreferences, sortPreferencesState]);

  useEffect(() => {
    let mounted = true;

    const loadRankGridPreferences = async () => {
      try {
        const response = await RankingAPI.getRankGridPreferences();
        if (!mounted) return;

        if (!response || response.status >= 400) {
          return;
        }

        const preferences = extractPreferences(response);
        setSortPreferencesState({
          sort_by: preferences.sort_by,
          sort_dir: preferences.sort_dir,
        });
        setWidthMap(toWidthMap(preferences.column_layout));
        setFrozenMap(toFrozenMap(preferences.column_layout));
        writeCachedColumnLayout(preferences.column_layout);
      } catch {
        if (!mounted) return;
      } finally {
        if (mounted) {
          hasLoadedPreferencesRef.current = true;
          setPreferencesReady(true);
        }
      }
    };

    hasLoadedPreferencesRef.current = false;
    loadRankGridPreferences();

    return () => {
      mounted = false;
      if (saveTimeoutRef.current) {
        window.clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [gridPreferencesVersion]);

  const persistPreferences = useCallback(
    async (nextWidthMap) => {
      if (!hasLoadedPreferencesRef.current || allColumnKeys.length === 0) {
        return;
      }

      const renderedWidthMap = columns.reduce((accumulator, column) => {
        const columnKey = column?.key || column?.dataIndex;
        if (!columnKey || columnKey === "sort") {
          return accumulator;
        }

        const widthFromColumn = parseColumnWidth(column.width);
        if (Number.isFinite(widthFromColumn)) {
          accumulator[columnKey] = widthFromColumn;
        }
        return accumulator;
      }, {});

      const keysToPersist = Array.from(
        new Set([
          ...allColumnKeys,
          ...Object.keys(nextWidthMap),
          ...Object.keys(renderedWidthMap),
          ...Object.keys(defaultWidthMap),
          ...Object.keys(frozenMapRef.current),
        ]),
      );

      const columnLayout = buildColumnLayout({
        keysToPersist,
        widthMap: nextWidthMap,
        renderedWidthMap,
        defaultWidthMap,
        frozenMap: frozenMapRef.current,
        minWidth,
      });

      const payload = {
        ...(sortPreferences ?? sortPreferencesRef.current),
        column_layout: columnLayout,
      };

      try {
        const response = await RankingAPI.updateRankGridPreferences(payload);
        if (response?.data?.status === true || response?.status === true) {
          writeCachedColumnLayout(columnLayout);
          messageToast?.(
            "success",
            response?.data?.message || "Column width updated successfully.",
          );
        } else {
          messageToast?.(
            "error",
            response?.data?.message || "Failed to update column width.",
          );
        }
      } catch {
        messageToast?.("error", "Failed to update column width.");
      }
    },
    [allColumnKeys, columns, defaultWidthMap, messageToast, minWidth, sortPreferences],
  );

  const schedulePersist = useCallback(
    (nextWidthMap) => {
      if (!hasLoadedPreferencesRef.current) return;

      if (saveTimeoutRef.current) {
        window.clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = window.setTimeout(() => {
        persistPreferences(nextWidthMap);
      }, 300);
    },
    [persistPreferences],
  );

  const updateWidth = useCallback(
    (columnKey, nextWidth) => {
      if (!columnKey || !Number.isFinite(nextWidth)) return;

      const clampedWidth = Math.max(minWidth, Math.round(nextWidth));

      setWidthMap((previous) => {
        // Dragging further once the column is already clamped at the minimum
        // produces the same width every frame. Keep the previous object so
        // React bails out instead of re-rendering (and re-persisting) a no-op.
        if (previous[columnKey] === clampedWidth) {
          return previous;
        }

        const updated = {
          ...previous,
          [columnKey]: clampedWidth,
        };
        widthMapRef.current = updated;
        return updated;
      });
    },
    [minWidth],
  );

  const getResizeMouseDownHandler = useCallback(
    (columnKey, currentWidth) => (event) => {
      if (!columnKey) return;

      event.preventDefault();
      event.stopPropagation();

      const startX = event.clientX;
      const baseWidth = Number.isFinite(currentWidth)
        ? currentWidth
        : minWidth;

      const onMouseMove = (moveEvent) => {
        userResizedRef.current = true;
        const delta = moveEvent.clientX - startX;
        updateWidth(columnKey, baseWidth + delta);
      };

      const onMouseUp = (upEvent) => {
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);

        if (!userResizedRef.current) return;

        userResizedRef.current = false;
        const delta = upEvent.clientX - startX;
        const finalWidth = Math.max(minWidth, Math.round(baseWidth + delta));

        // The pointer moved but the column did not: dragging past the minimum
        // width, or back to where the drag started, leaves the layout exactly
        // as it was. Nothing changed, so there is nothing to save.
        if (finalWidth === baseWidth) return;

        const updatedWidthMap = {
          ...widthMapRef.current,
          [columnKey]: finalWidth,
        };

        widthMapRef.current = updatedWidthMap;
        setWidthMap(updatedWidthMap);
        schedulePersist(updatedWidthMap);
      };

      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
    },
    [minWidth, schedulePersist, updateWidth],
  );

  const isColumnFrozen = useCallback(
    (columnKey) => {
      if (!columnKey || columnKey === "sort") {
        return false;
      }
      return Boolean(frozenMap[columnKey]);
    },
    [frozenMap],
  );

  const columnsWithResize = useMemo(() => {
    const selectionColumnWidth = 48;

    const resolveWidth = (column, columnKey) => {
      if (columnKey === "sort") {
        return parseColumnWidth(column.width) || 40;
      }

      const widthFromColumn = parseColumnWidth(column.width);
      const resolvedWidth = Number.isFinite(widthMap[columnKey])
        ? widthMap[columnKey]
        : widthFromColumn;

      return Number.isFinite(resolvedWidth) ? resolvedWidth : minWidth;
    };

    const applyStickyFrozenCell = ({
      column,
      columnKey,
      stickyLeft,
      resolvedWidth,
      stickyIndex,
      isLastFrozen,
      previousOnHeaderCell,
      previousOnCell,
    }) => ({
      ...column,
      width: resolvedWidth,
      // Avoid antd `fixed` — it draws its own fix-left-last edge shadow.
      onHeaderCell: (col) => {
        const existingHeaderProps = previousOnHeaderCell
          ? previousOnHeaderCell(col)
          : {};

        return {
          ...existingHeaderProps,
          className: [
            "ranking-frozen-cell",
            isLastFrozen ? "ranking-frozen-cell--last" : null,
            existingHeaderProps.className,
          ]
            .filter(Boolean)
            .join(" "),
          style: {
            ...(existingHeaderProps.style || {}),
            position: "sticky",
            left: stickyLeft,
            zIndex: 20 + stickyIndex,
          },
          ...(columnKey !== "sort"
            ? {
                onResizeMouseDown: getResizeMouseDownHandler(
                  columnKey,
                  resolvedWidth,
                ),
              }
            : {}),
        };
      },
      onCell: (record, rowIndex) => {
        const existingCellProps = previousOnCell
          ? previousOnCell(record, rowIndex)
          : {};

        return {
          ...existingCellProps,
          className: [
            "ranking-frozen-cell",
            isLastFrozen ? "ranking-frozen-cell--last" : null,
            existingCellProps.className,
          ]
            .filter(Boolean)
            .join(" "),
          style: {
            ...(existingCellProps.style || {}),
            position: "sticky",
            left: stickyLeft,
            zIndex: 10 + stickyIndex,
          },
        };
      },
    });

    const applyResizableCell = (column, columnKey, resolvedWidth) => {
      const previousOnHeaderCell = column.onHeaderCell;

      // Drag-handle column is fixed width — no resize affordance (matches the
      // frozen/sticky path, which also skips "sort").
      if (columnKey === "sort") {
        return {
          ...column,
          ...(Number.isFinite(resolvedWidth) ? { width: resolvedWidth } : {}),
        };
      }

      return {
        ...column,
        ...(Number.isFinite(resolvedWidth) ? { width: resolvedWidth } : {}),
        onHeaderCell: (col) => {
          const existingHeaderProps = previousOnHeaderCell
            ? previousOnHeaderCell(col)
            : {};

          return {
            ...existingHeaderProps,
            onResizeMouseDown: getResizeMouseDownHandler(
              columnKey,
              resolvedWidth,
            ),
          };
        },
      };
    };

    // Sticky freeze only works as a contiguous left block. Keep sort + frozen
    // columns on the left, then render unfrozen columns after them.
    const sortColumn = columns.find(
      (column) => (column?.key || column?.dataIndex) === "sort",
    );
    const dataColumns = columns.filter((column) => {
      const columnKey = column?.key || column?.dataIndex;
      return columnKey && columnKey !== "sort";
    });
    const frozenColumns = dataColumns.filter((column) =>
      isColumnFrozen(column?.key || column?.dataIndex),
    );
    const unfrozenColumns = dataColumns.filter(
      (column) => !isColumnFrozen(column?.key || column?.dataIndex),
    );

    const orderedColumns = [
      ...(sortColumn ? [sortColumn] : []),
      ...frozenColumns,
      ...unfrozenColumns,
    ];

    const hasFrozenColumns = frozenColumns.length > 0;
    // Last sticky col = last user-frozen col (or sort if somehow only sticky).
    const lastStickyColumnKey = hasFrozenColumns
      ? frozenColumns.length
        ? frozenColumns[frozenColumns.length - 1]?.key ||
          frozenColumns[frozenColumns.length - 1]?.dataIndex
        : "sort"
      : null;
    let cumulativeLeft = selectionColumnWidth;
    let stickyIndex = 0;

    return orderedColumns.map((column) => {
      const columnKey = column?.key || column?.dataIndex;
      if (!columnKey) {
        return column;
      }

      const resolvedWidth = resolveWidth(column, columnKey);
      const shouldStick =
        hasFrozenColumns &&
        (columnKey === "sort" || isColumnFrozen(columnKey));

      if (!shouldStick) {
        return applyResizableCell(column, columnKey, resolvedWidth);
      }

      const stickyLeft = cumulativeLeft;
      cumulativeLeft += resolvedWidth;
      stickyIndex += 1;

      return applyStickyFrozenCell({
        column,
        columnKey,
        stickyLeft,
        resolvedWidth,
        stickyIndex,
        isLastFrozen: columnKey === lastStickyColumnKey,
        previousOnHeaderCell: column.onHeaderCell,
        previousOnCell: column.onCell,
      });
    });
  }, [columns, getResizeMouseDownHandler, isColumnFrozen, minWidth, widthMap]);

  const hasFrozenColumns = useMemo(
    () =>
      columns.some((column) => {
        const columnKey = column?.key || column?.dataIndex;
        return columnKey && columnKey !== "sort" && isColumnFrozen(columnKey);
      }),
    [columns, isColumnFrozen],
  );

  return {
    columnsWithResize,
    hasFrozenColumns,
    isColumnFrozen,
    preferencesReady,
  };
};
